import { Box, Stack } from "@mui/material";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";

import { useTableSearchParams } from "Forms/Hooks/useTableSearchParams";
import { SortOrder, UserListSortColumn } from "Infrastructure/Api/Api";
import { useAppNavigate } from "Infrastructure/Routing/useAppNavigate";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { TableSearchInput } from "Projects/Components/TableSearchInput";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";
import { useUsersQuery } from "Users/Api/Queries/useUsersQuery";
import {
  USERS_SORTABLE_COLUMNS,
  useUsersTableColumns,
} from "Users/Components/useUsersTableColumns";

const SORT_COLUMN_MAP: Record<string, UserListSortColumn> = {
  firstName: UserListSortColumn.FirstName,
  lastName: UserListSortColumn.LastName,
  login: UserListSortColumn.Login,
};

export const UsersTable = (): React.ReactNode => {
  const { t } = useResource();
  const navigate = useAppNavigate();

  const {
    search,
    debouncedSearch,
    onSearchChange,
    pageIndex,
    pageSize,
    onPaginationChange,
    sorting,
    onSortingChange,
  } = useTableSearchParams({ validSortIds: [...USERS_SORTABLE_COLUMNS] });

  const sortBy = sorting[0]?.id ? SORT_COLUMN_MAP[sorting[0].id] : undefined;
  const sortOrder = sorting[0]?.desc
    ? SortOrder.Descending
    : SortOrder.Ascending;

  const { data, isLoading } = useUsersQuery({
    offset: pageIndex * pageSize,
    limit: pageSize,
    searchQuery: debouncedSearch || null,
    sortBy,
    sortOrder,
  });

  const users = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  const columns = useUsersTableColumns();

  const table = useReactTable({
    data: users,
    columns,
    getRowId: (row) => row.userID,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    rowCount: totalCount,
    state: { pagination: { pageIndex, pageSize }, sorting },
    onSortingChange,
    onPaginationChange,
  });

  return (
    <Box>
      <Stack
        direction="row"
        spacing={Spacing.S}
        sx={{ marginBottom: Spacing.M }}
      >
        <Box sx={{ flex: 1 }}>
          <TableSearchInput
            name="user-search"
            value={search}
            onChange={onSearchChange}
            placeholder={t(Resources.Users.SearchUsersPlaceholder)}
          />
        </Box>
      </Stack>
      <BlTanstackTable
        table={table}
        isLoading={isLoading}
        totalCount={totalCount}
        onRowClick={(row) =>
          navigate(AppRouting.UserDetail.path(row.original.userID))
        }
      />
    </Box>
  );
};
